<template>
<div class="users data--cards">
    <div class="page__header">
        <div class="page__title">
            {{ $t(`pageTitles.${"users"}`) }}
            
        </div>
        <div class="page__right">
            <div class="selected-filters" v-if="selectedTeamId !== null || selectedStatus !== null">
                <div v-for="teamId in selectedTeamIds" :key="`team${teamId}`" class="selected-filters__filter">
                    <span>Team: {{ getTeamDisplayName(teamId) }}</span>
                    <div class="selected-filters__remove" @click="removeTeam(teamId)"></div> 
                </div>

                <div class="selected-filters__filter" v-if="selectedStatus !== null">
                    <span>Status: {{ selectedStatus == 1 ? 'Active' : 'Inactive' }}</span>
                    <div class="selected-filters__remove" @click="removeStatusTag()"></div>
                </div>
            </div> 
            <form class="search search--filter" @submit.prevent="applyFilters()">
                <div class="search__wrapper">
                    <input class="search__search" :class="{ 'search--active': searchKeywords.length }" type="text" v-model="searchKeywords" />
                    <div class="search__icon"></div>
                    <div class="search__splitter"></div>
                </div>
                <div class="search__filter" ref="filterToggler" @click="showSearchFilter = !showSearchFilter"></div>
                <SearchFilter v-if="showSearchFilter" :filterToggler="filterToggler" :searchType="'user'" :toggleSearchFilter="
                (state) => {
                  showSearchFilter = state;
                }
              " />
            </form>

            <button v-if="canCreateUser" class="users__button button button--default" @click="openCreateUser">
                {{ $t(`settings.user.${"addUserBtn"}`) }}
            </button>
        </div>
    </div>

    <div class="table">
        <Loading v-if="usersLoading" />
        <template v-else>
            <NoData v-if="userCollectionError?.status === 403" type="no-permission" />
            <NoData v-else-if="!users.length" :type="'users'" />
            <template v-else>
                <div class="table__header box box--default">
                    <div class="table__label table__label--small"></div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"name"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"contact"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"team"}`) }}
                    </div>
                    <!-- <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"companyName"}`) }}
                    </div> -->
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"address"}`) }}
                    </div>
                    <div class="table__label table__label--small">
                        {{ $t(`users.${"tableHeader"}.${"zipCode"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"city"}`) }}
                    </div>
                    <div class="table__label table__label--medium">
                        {{ $t(`users.${"tableHeader"}.${"supervisor"}`) }}
                    </div>
                    <div class="table__label table__label--large">
                        {{ $t(`users.${"tableHeader"}.${"salesGroup"}`) }}
                    </div>
                    <div class="table__label table__label--small">
                        {{ $t(`users.${"tableHeader"}.${"account"}`) }}
                    </div>
                    <div class="table__label table__label--small" v-if="canManageMembership"></div>
                </div>

                <div class="table__body">
                    <User v-for="user in users" :key="user.id" :user="user" :selectedTeamId="selectedTeamId" :deleteUser="deleteUser" />
                </div>
            </template>
        </template>
    </div>
    <Pagination v-if="!usersLoading && users && users.length > 0" :limit="3" :data="usersPerPage" @pagination-change-page="getResults"></Pagination>
</div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import User from "@/components/Users/User";
import Loading from "@/partials/Loading";
import NoData from "@/partials/NoData";
import SearchFilter from "@/partials/SearchFilter";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Template refs
const filterToggler = ref(null);

// Reactive data
const searchKeywords = ref("");
const showSearchFilter = ref(false);
const selectedTeamIds = ref([]);
const selectedTeamId = computed(() => selectedTeamIds.value.length > 0 ? selectedTeamIds.value[0] : null);
const selectedStatus = ref(null);

const normalizeUserStatus = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = Number(value);
  return Number.isInteger(normalized) ? normalized : null;
};

const getTeamDisplayName = (teamId) => {
  const resolvedTeamId = Number(teamId);
  if (!Number.isInteger(resolvedTeamId) || resolvedTeamId <= 0) {
    return "All authorized teams";
  }

  const teamNameFromMembership = store.getters.membershipFor(resolvedTeamId)?.team?.name;
  if (teamNameFromMembership) return teamNameFromMembership;

  const teamNameFromAuthorizationMembership = Array.isArray(store.getters.authorizationMemberships)
    ? store.getters.authorizationMemberships.find((membership) => Number(membership?.team?.id) === resolvedTeamId)?.team?.name
    : null;
  if (teamNameFromAuthorizationMembership) return teamNameFromAuthorizationMembership;

  const teamNameFromList = Array.isArray(store.getters.teams)
    ? store.getters.teams.find((team) => Number(team?.id) === resolvedTeamId)?.name
    : null;
  if (teamNameFromList) return teamNameFromList;

  const userTeamName = Array.isArray(store.getters.loggedUser?.teams)
    ? store.getters.loggedUser.teams.find((team) => Number(team?.id) === resolvedTeamId)?.name
    : null;
  if (userTeamName) return userTeamName;

  const currentTeamId = Number(store.getters.currentTeamId);
  if (Number.isInteger(currentTeamId) && currentTeamId > 0 && currentTeamId === resolvedTeamId) {
    return store.getters.membershipFor(currentTeamId)?.team?.name || `Team ${resolvedTeamId}`;
  }

  return `Team ${resolvedTeamId}`;
};

// Computed properties
const usersLoading = computed(() => store.getters.usersLoading);
const usersPerPage = computed(() => store.getters.usersPerPage);
const users = computed(() => Array.isArray(usersPerPage.value?.data) ? usersPerPage.value.data : []); 
const userCollectionError = computed(() => store.getters.userCollectionError);

const isSuperAdmin = computed(() => store.getters.isSuperAdmin);

const canManageMembership = computed(() => {
  if (isSuperAdmin.value) return true;

  const userTeamsWithEditPerm = store.getters.getTeamsWithPermission("user_edit");
  const userTeamsWithDeletePerm = store.getters.getTeamsWithPermission("user_delete");
  return userTeamsWithEditPerm.length > 0 || userTeamsWithDeletePerm.length > 0;
});

const canCreateUser = computed(() => {
  if (isSuperAdmin.value) return true;

  const userTeamsWithCreatePerm = store.getters.getTeamsWithPermission("user_create");
  return userTeamsWithCreatePerm.length > 0;
});

// Methods
const syncFiltersFromRoute = (query) => {
  searchKeywords.value = query.search || "";

  const normalizedTeamIds = Array.isArray(query.teams)
    ? query.teams
    : typeof query.teams === "string" && query.teams.length
      ? query.teams.split(",")
      : [];

  selectedTeamIds.value = normalizedTeamIds
    .flatMap((teamId) => String(teamId).split(","))
    .map((teamId) => Number(teamId))
    .filter((teamId) => Number.isInteger(teamId) && teamId > 0);

  selectedStatus.value = normalizeUserStatus(query.active_users);
};

const removeTeam = (teamId) => {
  selectedTeamIds.value = selectedTeamIds.value.filter(id => id !== teamId);
  applyFilters();
};

const removeStatusTag = () => {
  selectedStatus.value = null;
  applyFilters();
};

const applyFilters = () => {
  const normalizedStatus = normalizeUserStatus(selectedStatus.value);

  router.replace({
    query: {
      ...route.query,
      page: 1,
      search: searchKeywords.value || undefined,
      teams: selectedTeamIds.value.length > 0 ? selectedTeamIds.value : undefined,
      active_users: normalizedStatus !== null ? normalizedStatus : undefined,
    }
  });
};

const deleteUser = ({ display, message, action, id }) => {
  store.commit("setDeleteModal", {
    display,
    message,
    action,
    id,
  });
};

const openCreateUser = () => {
  if (isSuperAdmin.value) {
    store.commit("setShowAddUserModal", true);
    return;
  }

  const userTeamsWithCreatePerm = store.getters.getTeamsWithPermission("user_create");
  if (userTeamsWithCreatePerm.length > 0) {
    store.commit("setShowAddUserModal", true);
  }
};

const searchUsers = async () => {
  const q = route.query;
  const normalizedStatus = normalizeUserStatus(q.active_users);
  const normalizedTeamIds = selectedTeamIds.value.length > 0
    ? selectedTeamIds.value.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
    : null;

  await store.dispatch("searchUsers", {
    search: q.search || "",
    teamId: normalizedTeamIds,
    active_users: normalizedStatus,
    page: Number(q.page) || 1,
  });
};

const getResults = async (paginationPage) => {
  const currentPage = Number(route.query.page) || 1;
  if (Number(paginationPage) === currentPage) return;

  router.replace({
    query: { ...route.query, page: paginationPage },
  });
};

// Watchers
watch(
  () => route.query,
  async (query, _oldQuery) => {
    if (!query.page) {
      router.replace({
        query: { ...query, page: 1 },
      });
      return;
    }

    if (_oldQuery && JSON.stringify(query) === JSON.stringify(_oldQuery)) {
      return;
    }

    syncFiltersFromRoute(query);

    await searchUsers();
  },
  { immediate: true, deep: true }
);

const hydrateTeamMetadata = async () => {
  if (!Array.isArray(store.getters.teams) || store.getters.teams.length === 0) {
    await store.dispatch("getTeams");
  }
};

hydrateTeamMetadata();

</script>

<style lang="scss" scoped>
.users {
    height: 100%;

    &__button {
        width: 190px;
    }

    .table {
        margin-bottom: 20px;
        height: calc(100% - 40px - 20px - 20px - 30px);
    }
}

.search-filter {
    max-width: 600px !important;
}
</style>
